import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  CardMedia,
  Chip,
  Grid,
  Modal,
  Tab,
  Tabs,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material'
import { alpha } from '@mui/material/styles'
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth'
import PlayLessonOutlinedIcon from '@mui/icons-material/PlayLessonOutlined'
import DeliveryDiningOutlinedIcon from '@mui/icons-material/DeliveryDiningOutlined'
import DinnerDiningOutlinedIcon from '@mui/icons-material/DinnerDiningOutlined'
import CreditCardIcon from '@mui/icons-material/CreditCard'
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined'
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined'
import QrCodeScannerSharpIcon from '@mui/icons-material/QrCodeScannerSharp'
import { useEffect, useState } from 'react'
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  ArcElement,
  Tooltip as ChartTooltip,
  Legend,
  Filler,
} from 'chart.js'
import { Line, Doughnut } from 'react-chartjs-2'
import { getDashBoard } from '../../services/dashboard'
import { getdaysTimeStamp, startAndEndTime } from '../../utils'
import { DatePickerComponent } from '../billing-history'
import { IDashBoardAnalytics } from '../../all-types'
import { useAppSelector } from '../../redux-store/hooks'
import { downloadBill } from '../../services'
import { AppConfig } from '../../config'

ChartJS.register(
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  ArcElement,
  ChartTooltip,
  Legend,
  Filler
)

type TabValue = 'YESTERDAY' | 'TODAY' | 'WEEK' | 'MONTH'
type ProductSalesCount = {
  productName: string
  productImage: string
  saleCount: number
}

const placeholderImg =
  'data:image/svg+xml;utf8,' +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="200" height="150">
      <rect width="100%" height="100%" fill="#f2f2f2"/>
      <text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="#999" font-family="Arial" font-size="14">No image</text>
    </svg>`
  )

const Dashboard: React.FC = () => {
  const today = new Date()
  const { startTimeStamp, endTimeStamp } = startAndEndTime(today, today)

  const theme = useTheme()
  const [data, setData] = useState<Partial<IDashBoardAnalytics>>({})
  const [selectedTab, setSelectedTab] = useState<TabValue>('TODAY')
  const [startTime, setStartTime] = useState<number>(startTimeStamp)
  const [endTime, setEndTime] = useState<number>(endTimeStamp)
  const [openDatePicker, setOpenDatePicker] = useState<boolean>(false)
  const products = useAppSelector((state) => state.allproducts.data)

  const doughnutLabel = data?.paymentMethods ? Object.keys(data?.paymentMethods) : []
  const doughnuAmountData = data?.paymentMethods
    ? Object.entries(data.paymentMethods).map(([_, value]) => value.amount)
    : []
  const totalAmount = doughnuAmountData.reduce((acc, cur) => acc + cur, 0)

  const handleTabChange = (_: React.SyntheticEvent, newValue: TabValue) => {
    setSelectedTab(newValue)
    handleTabDisplay(newValue)
  }

  const handleDownloadReport = async () => {
    try {
      const clientId = AppConfig.getCLientId()
      if (!clientId) {
        console.error('Client ID is not defined')
        throw new Error('Client ID is not defined')
      }
      await downloadBill(clientId, String(startTime), String(endTime))
    } catch (e) {
      console.error('Error downloading report:', e)
    }
  }

  const handleTabDisplay = (day: TabValue) => {
    const { startTimeStamp, endTimeStamp } = getdaysTimeStamp(day)
    setStartTime(startTimeStamp)
    setEndTime(endTimeStamp)
  }

  useEffect(() => {
    const getData = async () => {
      try {
        const response = await getDashBoard(startTime, endTime)
        setData(response?.data?.data)
      } catch (e) {
        console.log(e)
      }
    }
    getData()
  }, [startTime, endTime, products])

  const formattedHourLabel = data?.salesByTime?.labels.map((label) => {
    const hour = parseInt(label, 10)
    const date = new Date()
    date.setHours(hour, 0, 0)
    return date.toLocaleTimeString('en-IN', { hour: 'numeric', hour12: true })
  })

  const lineData = {
    labels: formattedHourLabel,
    datasets: [
      {
        label: 'Sales',
        data: data?.salesByTime?.data,
        borderColor: theme.palette.color10.main,
        tension: 0.4,
        pointBackgroundColor: theme.palette.color10.main,
        backgroundColor: 'rgba(244, 67, 54, 0.1)',
        fill: true,
      },
    ],
  }

  const doughnutData = {
    labels: doughnutLabel,
    datasets: [
      {
        label: 'Amount',
        data: doughnuAmountData,
        cutout: '60%',
        backgroundColor: [
          theme.palette.color9.main,
          '#A92C32',
          theme.palette.CommonBlack.main,
        ],
        borderWidth: 4,
        hoverBorderColor: theme.palette.color12.main,
        borderColor: theme.palette.color12.main,
      },
    ],
  }

  const StatCard = ({
    icon,
    label,
    value,
    progress,
    color,
    height,
  }: {
    icon: React.ReactNode
    label: string
    value: string | number
    progress: number
    color: string
    height: string
  }) => (
    <Card sx={{ height }} elevation={3}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              bgcolor: color,
              borderRadius: '10px',
              color: 'white',
              width: 30,
              height: 30,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            {icon}
          </Box>
          <Typography variant="h6" fontWeight="bold" color="textSecondary">
            {label}
          </Typography>
        </Box>

        <Typography variant="h6" my={1} fontWeight="bold" textAlign="center">
          {value}
        </Typography>

        <Box
          sx={{
            width: '100%',
            bgcolor: 'color15.main',
            height: 2,
            borderRadius: 1,
            overflow: 'hidden',
          }}
        >
          <Box sx={{ width: `${progress}%`, bgcolor: color, height: 1 }} />
        </Box>
      </CardContent>
    </Card>
  )

  // ------------------------
  // TILE VERSION of product sales (replaces the old table)
  // ------------------------
  const getSalesOfProduct = () => {
    const salesCount: ProductSalesCount[] = []
    try {
      products?.forEach((p) => {
        if (data?.salesByProduct && data.salesByProduct[p.productId] >= 1) {
          salesCount.push({
            productName: p.name,
            productImage: p.image || placeholderImg,
            saleCount: data.salesByProduct[p.productId],
          })
        }
      })
      const sortedProducts = [...salesCount].sort(
        (a, b) => (b.saleCount ?? 0) - (a.saleCount ?? 0)
      )

      return (
        <Grid container spacing={2} mt={1}>
          {sortedProducts.map((p, index) => (
            <Grid item key={`${p.productName}-${index}`} xs={12} sm={6} md={4} lg={3} xl={2}>
              <Card
                elevation={3}
                sx={{
                  height: '100%',
                  borderRadius: 3,
                  overflow: 'hidden',
                  transition: 'transform 120ms ease, box-shadow 120ms ease',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: 6,
                  },
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <CardActionArea sx={{ height: '100%' }}>
                  <Box sx={{ position: 'relative' }}>
                    <CardMedia
                      component="img"
                      height="150"
                      image={p.productImage || placeholderImg}
                      alt={p.productName}
                      onError={(e) => ((e.target as HTMLImageElement).src = placeholderImg)}
                      loading="lazy"
                      sx={{
                        objectFit: 'contain',
                        bgcolor: alpha(theme.palette.primary.light, 0.08),
                      }}
                    />
                    {index < 3 && (
                      <Chip
                        label={`#${index + 1}`}
                        size="small"
                        color={index === 0 ? 'success' : index === 1 ? 'warning' : 'info'}
                        sx={{ position: 'absolute', top: 8, left: 8, borderRadius: 1.5 }}
                      />
                    )}
                  </Box>

                  <CardContent sx={{ display: 'grid', gap: 0.5 }}>
                    <Typography
                      variant="subtitle1"
                      fontWeight={700}
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                      title={p.productName}
                    >
                      {p.productName}
                    </Typography>

                    <Tooltip title="Units sold in selected period">
                      <Typography
                        variant="h3"
                        component="div"
                        sx={{
                          lineHeight: 1.1,
                          fontWeight: 800,
                          letterSpacing: 0.5,
                          fontSize: { xs: '2rem', sm: '2.25rem', md: '2.5rem' },
                        }}
                      >
                        {p.saleCount.toLocaleString('en-IN')}
                      </Typography>
                    </Tooltip>

                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                      items sold
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}

          {sortedProducts.length === 0 && (
            <Grid item xs={12}>
              <Box
                sx={{
                  p: 4,
                  textAlign: 'center',
                  borderRadius: 3,
                  bgcolor: alpha(theme.palette.info.light, 0.12),
                  border: `1px dashed ${alpha(theme.palette.info.main, 0.4)}`,
                }}
              >
                <Typography variant="h6" fontWeight={700} gutterBottom>
                  No sales found for the selected period
                </Typography>
                <Typography color="text.secondary">
                  Try adjusting the date range or check back later.
                </Typography>
              </Box>
            </Grid>
          )}
        </Grid>
      )
    } catch (e) {
      console.error(e)
      return null
    }
  }

  return (
    <Box px={3} py={1}>
      {/* Header */}
      <Box display={'flex'} justifyContent={'space-between'}>
        <Typography variant="h5" fontWeight={700} gutterBottom>
          DASHBOARD
        </Typography>
        {/* Tabs and Icon */}
        <Box display={'flex'} gap={2} alignItems={'center'}>
          <Button
            variant="contained"
            startIcon={<DownloadOutlinedIcon />}
            onClick={handleDownloadReport}
            sx={{ textTransform: 'none' }}
          >
            Download Report
          </Button>
          <Box onClick={() => setOpenDatePicker(true)} sx={{ cursor: 'pointer' }}>
            <CalendarMonthIcon fontSize="large" />
          </Box>
          <Tabs
            indicatorColor="primary"
            value={selectedTab}
            onChange={handleTabChange}
            sx={{ borderBottom: '1px solid', borderColor: 'divider' }}
          >
            <Tab value={'YESTERDAY'} label={'Yesterday'} sx={{ textTransform: 'none', color: 'color15.main' }} />
            <Tab value={'TODAY'} label={'Today'} sx={{ textTransform: 'none', color: 'color15.main' }} />
            <Tab value={'WEEK'} label="Week" sx={{ textTransform: 'none', color: 'color15.main' }} />
            <Tab value={'MONTH'} label="Month" sx={{ textTransform: 'none', color: 'color15.main' }} />
          </Tabs>
        </Box>
      </Box>

      <Grid container spacing={3} mt={2}>
        {/* Line Chart */}
        <Grid item xs={14} md={5} lg={5}>
          <Card sx={{ height: 300 }} elevation={3}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={'bold'} pl={2} gutterBottom>
                Daily Sales
              </Typography>
              <Box height={240}>
                <Line
                  data={lineData}
                  options={{
                    plugins: { legend: { display: false } },
                    responsive: true,
                    scales: {
                      x: {
                        border: { display: false },
                        grid: { display: false },
                      },
                      y: {
                        beginAtZero: true,
                        border: { display: false },
                        grid: {},
                      },
                    },
                  }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Doughnut Chart */}
        <Grid item xs={10} md={4} lg={4}>
          <Card
            elevation={3}
            sx={{
              height: 300,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
            }}
          >
            <CardContent>
              <Typography variant="subtitle1" fontWeight={'bold'} gutterBottom>
                Total Amount
              </Typography>
              <Box width={200} height={200} mx="auto" sx={{ position: 'relative' }}>
                {totalAmount != 0 && (
                  <Typography
                    variant="subtitle2"
                    fontWeight={'bold'}
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                    }}
                  >
                    ₹{totalAmount}
                  </Typography>
                )}
                <Doughnut
                  data={doughnutData}
                  options={{
                    plugins: { legend: { display: false } },
                    responsive: true,
                    maintainAspectRatio: true,
                  }}
                />
              </Box>
              <Box display={'flex'} justifyContent={'center'} gap={2} mt={1}>
                {doughnutLabel.map((item) => (
                  <Box key={item} display="flex" flexDirection={'row'} alignItems={'center'} gap={1}>
                    <Box
                      width={10}
                      height={10}
                      borderRadius="50%"
                      bgcolor={
                        item === 'cash'
                          ? theme.palette.color9.main
                          : item === 'upi'
                          ? '#A92C32'
                          : theme.palette.common.black
                      }
                    />
                    <Typography fontWeight={'bold'} textTransform={'capitalize'}>
                      {item}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Info Cards */}
        <Grid
          item
          xs={6}
          md={3}
          lg={3}
          display={'flex'}
          flexDirection={'column'}
          justifyContent={'space-between'}
          gap={2}
        >
          <StatCard
            icon={<PlayLessonOutlinedIcon />}
            label="Total Orders"
            value={`${data?.totalBills}`}
            progress={60}
            color="color3.main"
            height="45%"
          />
          <StatCard
            icon={<DinnerDiningOutlinedIcon />}
            label="Dine-In Orders"
            value={`${data?.orderTypes?.dineIn?.count}`}
            progress={60}
            color="primary.main"
            height="45%"
          />
        </Grid>
      </Grid>

      <Grid container spacing={2} mt={2}>
        <Grid item xs={12} md={3}>
          <StatCard
            icon={<PaymentsOutlinedIcon />}
            label="Cash Payment"
            value={`${data?.paymentMethods?.cash?.count}`}
            progress={60}
            color="color4.main"
            height="100%"
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <StatCard
            icon={<QrCodeScannerSharpIcon />}
            label="UPI Payment"
            value={`${data?.paymentMethods?.upi?.count}`}
            progress={60}
            color="occupiedTable.main"
            height="100%"
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <StatCard
            icon={<CreditCardIcon />}
            label="Card Payment"
            value={`${data?.paymentMethods?.card?.count}`}
            progress={60}
            color="color5.main"
            height="100%"
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <StatCard
            icon={<DeliveryDiningOutlinedIcon />}
            label="TakeAway Orders"
            value={`${data?.orderTypes?.takeAway?.count}`}
            progress={60}
            color="color6.main"
            height="100%"
          />
        </Grid>
      </Grid>

      {products.length > 0 && data && (
        <>
          <Box mt={3} mb={1}>
            <Typography variant="subtitle1" fontWeight="bold">
              Product Sales
            </Typography>
          </Box>
          {getSalesOfProduct()}
        </>
      )}

      {/* Date Picker Modal */}
      <Modal
        open={openDatePicker}
        onClose={() => setOpenDatePicker(false)}
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      >
        <Box
          sx={{
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
            minWidth: 300,
            outline: 'none',
          }}
        >
          <DatePickerComponent
            selectRange={(start, end) => {
              setStartTime(start)
              setEndTime(end)
              setOpenDatePicker(false)
            }}
            onClose={() => setOpenDatePicker(false)}
          />
        </Box>
      </Modal>
    </Box>
  )
}

export { Dashboard }
